import { ethers } from "ethers";
import {
  getLedgerSignedTransaction,
  getProvider,
} from "./shared";
import USDC from "./contracts/1.0.0/usdc";

const GENERIC_METAKEEP_DISABLED_MESSAGE =
  "Generic MetaKeep Lambda execution is disabled. Use a typed backend operation endpoint.";


class WalletAdapter {
  constructor(state) {
    this.state = state;
    this.provider = null;
    this.signer = null;
  }

  async initialize() {
    const accountType = this.state.currentAccount?.type;
    const accountProvider = this.state.currentAccount?.provider;
    const accountAddress = this.state.currentAccount?.address;
    this.provider = await getProvider(
      null,
      null,
      accountType,
      this.state.networkId,
      accountProvider
    );
    if (this.provider?.getSigner) {
      try {
        this.signer = accountAddress
          ? this.provider.getSigner(accountAddress)
          : this.provider.getSigner();
      } catch (err) {
        console.log(err);
      }
    }
  }

  async executeMethod(contractAddress, abi, methodName, params = [], txOptions = {}) {
    if (!ethers.utils.isAddress(contractAddress)) {
      throw new Error("Invalid contract address provided to executeMethod.");
    }
    const normalizedAddress = ethers.utils.getAddress(contractAddress);
    const contract = new ethers.Contract(normalizedAddress, abi, this.signer);
    if (typeof contract[methodName] !== "function") {
      throw new Error(`Unknown contract method: ${methodName}`);
    }

    if (this.state.currentAccount.type === "MetaKeep") {
      throw new Error(GENERIC_METAKEEP_DISABLED_MESSAGE);
    } else if (this.state.currentAccount.type === "Ledger") {
      const data = contract.interface.encodeFunctionData(methodName, params);
      const { signedTx } = await getLedgerSignedTransaction({
        contractAddress: normalizedAddress,
        data,
        provider: this.provider,
        currentAccountAddress: this.state.currentAccount.address,
        networkId: this.state.networkId,
        derivationPath: this.state.currentAccount.derivationPath,
      });
      const response = await this.provider.sendTransaction(signedTx);
      const receipt = await response.wait();
      await this.provider.getBlockNumber();
      return { response, receipt };
    } else if (this.state.currentAccount.type === "Web3Auth") {
      // Gas is sponsored via Account Abstraction paymaster — no balance check needed
      // AA provider requires explicit value field; ethers omits it for non-payable calls
      const response = await contract[methodName](...params, {
        value: ethers.BigNumber.from(0),
        ...txOptions,
      });
      const receipt = await response.wait();
      await this.provider.getBlockNumber();
      return { response, receipt };
    } else {
      const txArgs = Object.keys(txOptions).length ? [...params, txOptions] : params;
      const response = await contract[methodName](...txArgs);
      const receipt = await response.wait();
      await this.provider.getBlockNumber();
      return { response, receipt };
    }
  }

  async executeMethodMetaKeepDeferred(
    contractAddress,
    abi,
    methodName,
    params = []
  ) {
    if (this.state.currentAccount.type !== "MetaKeep") {
      return this.executeMethod(contractAddress, abi, methodName, params);
    }

    throw new Error(GENERIC_METAKEEP_DISABLED_MESSAGE);
  }

  async signTypedDataV4(typedData) {
    const account = this.state.currentAccount || {};
    const address = account.address;
    if (!address) {
      throw new Error("Missing wallet address for game authentication.");
    }

    if (account.sdk && typeof account.sdk.signTypedData === "function") {
      return account.sdk.signTypedData(typedData);
    }

    const rawProvider =
      account.provider ||
      this.provider?.provider ||
      this.provider ||
      (typeof window !== "undefined" ? window.ethereum : null);
    if (rawProvider && typeof rawProvider.request === "function") {
      return rawProvider.request({
        method: "eth_signTypedData_v4",
        params: [address, JSON.stringify(typedData)],
      });
    }

    if (this.signer && typeof this.signer._signTypedData === "function") {
      const { EIP712Domain, ...types } = typedData.types || {};
      return this.signer._signTypedData(typedData.domain, types, typedData.message);
    }

    throw new Error("Connected wallet cannot sign typed game authentication data.");
  }

  async estimateGasFee(contractAddress, abi, methodName, params = []) {
    if (!ethers.utils.isAddress(contractAddress)) {
      throw new Error("Invalid contract address provided to estimateGasFee.");
    }
    const normalizedAddress = ethers.utils.getAddress(contractAddress);
    const contract = new ethers.Contract(normalizedAddress, abi, this.signer);
    let estimatedFee = ethers.constants.Zero;
    try {
      const gasEstimate = await contract.estimateGas[methodName](...params);
      const gasPrice = await this.provider.getGasPrice();
      estimatedFee = gasEstimate.mul(gasPrice);
      return { estimatedFee, status: "success" };
    } catch (err) {
      console.warn("Gas estimation failed, skipping fee check.", err);
      return { estimatedFee: 0, status: "error" };
    }
  }

  async approve(contractAddress, spender, amount) {
    if (!ethers.utils.isAddress(contractAddress)) {
      throw new Error("Invalid USDC contract address supplied.");
    }
    if (!ethers.utils.isAddress(spender)) {
      throw new Error("Invalid spender address supplied.");
    }
    const normalizedAddress = ethers.utils.getAddress(contractAddress);
    const spenderAddress = ethers.utils.getAddress(spender);
    if (spenderAddress === ethers.constants.AddressZero) {
      throw new Error("Spender address cannot be zero address.");
    }
    if (amount === undefined || amount === null) {
      throw new Error("Invalid approval amount provided.");
    }
    try {
      ethers.BigNumber.from(amount);
    } catch (err) {
      throw new Error("Invalid approval amount provided.");
    }
    const usdcContract = new ethers.Contract(
      normalizedAddress,
      USDC.abi,
      this.signer
    );

    if (this.state.currentAccount.type === "Ledger") {
      const approveData = usdcContract.interface.encodeFunctionData("approve", [
        spenderAddress,
        amount,
      ]);

      const { signedTx } = await getLedgerSignedTransaction({
        contractAddress,
        data: approveData,
        provider: this.provider,
        currentAccountAddress: this.state.currentAccount.address,
        networkId: this.state.networkId,
        derivationPath: this.state.currentAccount.derivationPath,
      });

      const transactionResponse = await this.provider.sendTransaction(signedTx);
      await transactionResponse.wait();
    } else if (this.state.currentAccount.type === "Web3Auth") {
      const approveTx = await usdcContract.approve(spenderAddress, amount, { value: ethers.BigNumber.from(0) });
      await approveTx.wait();
    } else {
      const approveTx = await usdcContract.approve(spenderAddress, amount);
      await approveTx.wait();
    }
  }
}

export default WalletAdapter;
